/**
 * Fetches the icon images a style references so they can travel inside a QGIS
 * file (`.qlr` / `.qml`) as base64. QGIS cannot authenticate to the private icon
 * CDN, so a remote reference would draw nothing.
 */
import type { Style } from "geostyler-style";
import { resolveIconUrl } from "./cdnUtils";
import { iconImageRef, type QgisEmbeddedIcon } from "./geostylerToQgis";

/** Used when an image's size cannot be read (an SVG with no intrinsic size). */
const FALLBACK_ICON_SIZE_PX = 24;

function readAsDataUri(blob: Blob): Promise<string | null> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(typeof reader.result === "string" ? reader.result : null);
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(blob);
  });
}

/** Natural pixel size, as the web map draws the image at `icon-size` 1. */
function imageSize(dataUri: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth || FALLBACK_ICON_SIZE_PX, height: img.naturalHeight || FALLBACK_ICON_SIZE_PX });
    img.onerror = () => resolve({ width: FALLBACK_ICON_SIZE_PX, height: FALLBACK_ICON_SIZE_PX });
    img.src = dataUri;
  });
}

async function fetchIcon(ref: string): Promise<QgisEmbeddedIcon | null> {
  try {
    // window.fetch carries the CDN credentials (installCdnCredentialedFetch).
    const res = await window.fetch(resolveIconUrl(ref), { credentials: "include" });
    if (!res.ok) return null;
    const blob = await res.blob();
    if (!blob.type.startsWith("image/")) return null;
    const dataUri = await readAsDataUri(blob);
    if (!dataUri) return null;
    return { dataUri, ...(await imageSize(dataUri)) };
  } catch {
    return null;
  }
}

/**
 * Every Icon image in the style, fetched in parallel and keyed by its reference.
 * An icon that cannot be fetched is left out, and the export draws a fallback
 * marker in its place — an export is never blocked on an icon.
 */
export async function fetchQgisIcons(style: Style | undefined): Promise<Map<string, QgisEmbeddedIcon>> {
  const refs = new Set<string>();
  for (const rule of style?.rules ?? []) {
    for (const symbolizer of rule.symbolizers ?? []) {
      const ref = symbolizer.kind === "Icon" ? iconImageRef(symbolizer.image) : undefined;
      if (ref) refs.add(ref);
    }
  }
  const entries = await Promise.all([...refs].map(async (ref) => [ref, await fetchIcon(ref)] as const));
  return new Map(entries.filter((entry): entry is [string, QgisEmbeddedIcon] => entry[1] !== null));
}
